<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 class="page-title">{{ page.title }}</h1>
        <p class="nd-page-kicker">{{ labels.description }}</p>
      </div>
    </header>

    <v-card class="nd-section-card">
      <v-form ref="form" v-model="dataForm.validForm">
        <div class="nd-table-toolbar">
          <div>
            <div class="nd-table-toolbar__title">{{ labels.cardTitle }}</div>
            <div class="nd-table-toolbar__subtitle">{{ labels.cardSubtitle }}</div>
          </div>
        </div>

        <div class="nd-form-grid nd-form-grid--two">
          <v-text-field
            dense
            id="name"
            label="Nome"
            v-model="dataset.data.name"
            outlined
            :rules="[v => !!v || 'Nome é obrigatório']"
            hide-details="auto"
            required
          ></v-text-field>
          <v-text-field
            dense
            id="email"
            label="E-mail"
            v-model="dataset.data.email"
            outlined
            :rules="[v => !!v || 'E-mail é obrigatório']"
            hide-details="auto"
            required
          ></v-text-field>
          <v-select
            dense
            id="role"
            label="Cargo"
            v-model="dataset.data.role"
            :items="dataset.roles"
            item-text="name"
            item-value="id"
            outlined
            :loading="dataset.loadingRoles"
            :rules="[v => !!v || 'Cargo é obrigatório']"
            :no-data-text="labels.noRolesText"
            hide-details="auto"
            required
          ></v-select>
        </div>

        <div class="nd-form-actions">
          <v-btn id="btnBack" type="button" elevation="0" color="primary" outlined :to="labels.listRoute">
            Voltar
          </v-btn>
          <v-btn id="btnSave" type="button" elevation="0" color="primary" @click="saveAdmin" :loading="dataset.loading">
            Salvar
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AdminDetails",

  props: {
    dentistMode: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    page: {
      title: "",
      isNew: false,
    },
    dataset: {
      data: {
        name: null,
        email: null,
        role: null,
      },
      roles: [],
      breadcrumbItems: [],
      loading: false,
      loadingRoles: false,
    },
    dataForm: {
      validForm: true,
    },
  }),

  async created() {
    this.checkIfNew();
    await Promise.all([this.loadData(), this.loadRoles()]);
    this.setPageTitle();
    this.listBreadCrumbItems();
  },

  computed: {
    labels() {
      if (this.dentistMode) {
        return {
          description: "Configure o acesso operacional do dentista.",
          cardTitle: "Dados do dentista",
          cardSubtitle: "Informe nome, e-mail e o cargo clínico de acesso.",
          listRoute: "/dentistas",
          noRolesText: "Nenhum cargo de dentista encontrado. Crie um cargo com acesso de dentista em Cargos.",
          loadRolesError: "Ocorreu um erro ao carregar os cargos de dentista. Tente novamente mais tarde.",
          loadError: "Ocorreu um erro ao carregar as informações do dentista. Tente novamente mais tarde.",
          createConfirm: "Deseja criar o dentista?",
          updateConfirm: "Deseja atualizar o dentista?",
          createTitle: "Criar Dentista",
          updateTitle: "Atualizar Dentista",
          createSuccess: "Dentista criado com sucesso!",
          updateSuccess: "Dentista atualizado com sucesso!",
        };
      }

      return {
        description: "Configure o acesso operacional do usuário administrativo.",
        cardTitle: "Dados do administrador",
        cardSubtitle: "Informe nome, e-mail e perfil de acesso.",
        listRoute: "/administradores",
        noRolesText: "Nenhum cargo encontrado.",
        loadRolesError: "Ocorreu um erro ao carregar os cargos. Tente novamente mais tarde.",
        loadError: "Ocorreu um erro ao carregar as informações do administrador. Tente novamente mais tarde.",
        createConfirm: "Deseja criar o administrador?",
        updateConfirm: "Deseja atualizar o administrador?",
        createTitle: "Criar Administrador",
        updateTitle: "Atualizar Administrador",
        createSuccess: "Administrador criado com sucesso!",
        updateSuccess: "Administrador atualizado com sucesso!",
      };
    },
  },

  methods: {
    checkIfNew() {
      this.page.isNew = !this.$route.params.id;
    },

    setPageTitle() {
      if (this.dentistMode) {
        this.page.title = this.page.isNew ? "Novo Dentista" : "Detalhes do Dentista";
        return;
      }

      this.page.title = this.page.isNew ? "Novo Administrador" : "Detalhes do Administrador";
    },

    goBack() {
      this.$router.replace(this.labels.listRoute);
    },

    listBreadCrumbItems() {
      this.dataset.breadcrumbItems = [
        { text: "Homepage", disabled: false, to: "/home", exact: true },
        { text: this.dentistMode ? "Dentistas" : "Administradores", disabled: false, to: this.labels.listRoute, exact: true },
        {
          text: this.page.title,
          disabled: true,
          exact: true,
        },
      ];
    },

    async loadRoles() {
      this.dataset.loadingRoles = true;
      try {
        const params = { start: 0, limit: 100 };
        if (this.dentistMode) params.dentist = true;
        const result = await axios.get("/roles", { params });
        this.dataset.roles = result.data.data;
      } catch (error) {
        this.$dialog.notify.error(
          this.labels.loadRolesError,
          { position: "top-right", timeout: 2000 }
        );
      } finally {
        this.dataset.loadingRoles = false;
      }
    },

    async loadData() {
      if (this.page.isNew) return;

      this.dataset.loading = true;
      try {
        const result = await axios.get(`/admin/${this.$route.params.id}`);
        this.dataset.data = result.data.data;
        if (this.dataset.data.role && typeof this.dataset.data.role === "object") {
          this.dataset.data.role = this.dataset.data.role.id;
        }
      } catch (error) {
        this.$dialog.notify.error(
          this.labels.loadError,
          { position: "top-right", timeout: 2000 }
        );
      } finally {
        this.dataset.loading = false;
      }
    },

    async saveAdmin() {
      if (!this.$refs.form.validate()) {
        this.$dialog.notify.warn("Preencha todos os campos obrigatórios.", {
          position: "top-right",
          timeout: 2000,
        });
        return;
      }

      const confirmText = this.page.isNew ? this.labels.createConfirm : this.labels.updateConfirm;
      const confirmTitle = this.page.isNew ? this.labels.createTitle : this.labels.updateTitle;
      const successMessage = this.page.isNew ? this.labels.createSuccess : this.labels.updateSuccess;

      const proceed = await this.$dialog.confirm({
        text: confirmText,
        title: confirmTitle,
        actions: {
          false: "Cancelar",
          true: this.page.isNew ? "Criar" : "Atualizar",
        },
      });

      if (!proceed) return;

      this.dataset.loading = true;

      try {
        if (this.page.isNew) {
          const user = (this.$root.user && this.$root.user.data) || this.$root.user || {};
          const companyId = user.companyId || user.company_id || user.company?.id;
          const payload = { ...this.dataset.data };
          if (companyId) payload.company = companyId;
          await axios.post("/admin", payload);
        } else {
          await axios.patch(`/admin/${this.$route.params.id}`, this.dataset.data);
        }

        this.$dialog.notify.info(successMessage, {
          position: "top-right",
          timeout: 2000,
        });

        this.goBack();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 2000,
        });
      } finally {
        this.dataset.loading = false;
      }
    },
  },
};
</script>
